<template>
  <v-sheet
    data-testid="organizationSettings-currency"
    elevation="2"
    class="px-12 py-8 rounded white"
  >
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('common.currency') }}
      </p>
      <p
        class="mt-2 mb-5 mb-0 pa-0 grey--text text--darken-2"
        v-html="$t('organization_settings.currency_subtitle')"
      />
      <p
        class="pa-0 ma-0 grey--text font-weight-bold"
        data-testid="currency-textField-globalCurrency"
      >
        {{ $t('organization_settings.global_currency') }} 1.0
        {{ organization.currency }}
      </p>
    </div>
    <v-form ref="currency">
      <v-row>
        <v-col cols="6">
          <v-text-field
            outlined
            dense
            v-model="rate"
            placeholder="0,0"
            type="number"
            data-testid="currency-textField-rate"
            data-tourid="currency-textField-exchangeRate"
            :disabled="isLoading"
            :label="$t('organization_settings.exchange_rate')"
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-select
            outlined
            required
            dense
            v-model="exchangeCurrency"
            data-testid="currency-select-availableCurrencies"
            data-tourid="currency-select-availableCurrencies"
            item-text="text"
            :label="`${$t('organization_settings.available_site_currency')}*`"
            :placeholder="$t('common.select')"
            :items="filteredAvailableCurrencies"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="mt-n1">
        <v-col cols="2">
          <v-tooltip max-width="424px" top>
            <template v-slot:activator="{ on, attrs }">
              <p
                v-bind="attrs"
                v-on="on"
                class="caption primary--text mb-0 mt-n4 relative"
              >
                {{ $t('organization_settings.whats_this') }}
              </p>
            </template>
            {{ $t('organization_settings.currency_tooltip') }}
          </v-tooltip>
        </v-col>
      </v-row>
      <div class="text-right">
        <Button
          size="large"
          color="primary"
          data-testid="currency-button-addCurrency"
          data-tourid="currency-button-addCurrency"
          :loading="isLoading"
          :disabled="!canAddCurrency"
          :text="$t('organization_settings.add_currency')"
          @click="addCurrency"
        />
      </div>
    </v-form>
    <div class="mt-4" v-if="currencies && currencies.length">
      <p class="h6 mb-3 mt-0">
        {{ $t('organization_settings.added_currencies') }}
      </p>
      <template v-for="(exchangeCurrency, index) in currencies">
        <v-hover v-slot:default="{ hover }" :key="exchangeCurrency._id">
          <div
            data-testid="currency-row-addedCurrency"
            class="pa-2 d-flex justify-space-between"
            :class="{
              hover: hover && exchangeCurrency.isViewMode,
              grey: hover && exchangeCurrency.isViewMode,
              'lighten-5': hover && exchangeCurrency.isViewMode,
            }"
          >
            <v-row>
              <v-col v-if="exchangeCurrency.isViewMode" cols="8">
                <p
                  class="ma-0"
                  :data-testid="`currency-paragraph-addedCurrency${index}`"
                >
                  {{ getCurrencyText(exchangeCurrency) }}
                </p>
              </v-col>
              <v-col v-else cols="5">
                <v-text-field
                  outlined
                  required
                  dense
                  autofocus
                  class="pa-0 ma-0"
                  type="number"
                  data-testid="currency-textField-editCurrency"
                  @input="
                    currencyIsValid = !!exchangeCurrency.exchangeRateFromGlobal
                  "
                  v-model="exchangeCurrency.exchangeRateFromGlobal"
                  :label="`${$t('organization_settings.exchange_rate')}*`"
                  :rules="[
                    exchangeRate =>
                      !!exchangeRate ||
                      $t('validation.form.this_field_is_required'),
                  ]"
                  :disabled="isLoading"
                >
                </v-text-field>
              </v-col>
              <v-col cols="7">
                <v-select
                  outlined
                  required
                  dense
                  disabled
                  class="me-4"
                  data-testid="organizationSettings-select-currency"
                  item-text="text"
                  v-if="!exchangeCurrency.isViewMode"
                  v-model="exchangeCurrency.currency"
                  :label="
                    `${$t('organization_settings.available_site_currency')}*`
                  "
                  :placeholder="$t('common.select')"
                  :items="getEditCurrenciesOptions(exchangeCurrency)"
                ></v-select>
              </v-col>
            </v-row>
            <div>
              <div v-if="exchangeCurrency.isViewMode" class="d-flex">
                <v-icon
                  v-for="icon in view_mode_icons"
                  class="grey--text icon"
                  :data-testid="`currency-icon-${icon.type}`"
                  :key="icon.type"
                  :disabled="disableActions"
                  @click="handleClick(icon.type, exchangeCurrency)"
                >
                  {{ icon.name }}
                </v-icon>
              </div>
              <div v-else class="d-flex align-center">
                <v-icon
                  v-for="icon in edit_mode_icons"
                  class="grey--text icon mt-2"
                  :data-testid="`currency-icon-${icon.type}`"
                  :loading="isSaving"
                  :disabled="icon.type === 'save' && !currencyIsValid"
                  :key="icon.type"
                  @click="handleClick(icon.type, exchangeCurrency)"
                >
                  {{ icon.name }}
                </v-icon>
              </div>
            </div>
          </div>
        </v-hover>
      </template>
    </div>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isLoading"
      :title="$t('organization_settings.delete_currency_title')"
      :text="$t('organization_settings.delete_confirmation')"
      @cancel="showDeleteModal = false"
      @delete="deleteCurrency"
    />
  </v-sheet>
</template>
<script>
import availableCurrencies from '@/locales/currencies.json'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'Currency',

  components: {
    DeleteConfirmationModal,
    Button,
  },

  props: {
    organization: {
      required: true,
      type: Object,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      availableCurrencies: Object.values(availableCurrencies).map(
        ({ name, code, symbol_native }) => ({
          name,
          symbol_native,
          value: code,
          text: `${code} - ${name}`,
        })
      ),
      exchangeCurrency: null,
      rate: null,
      currencyToEdit: {},
      currencyToDelete: {},
      disableActions: false,
      showDeleteModal: false,
      isSaving: false,
      currencyIsValid: true,
      view_mode_icons: [
        { type: 'edit', name: 'create' },
        // The backend does not support the delete option yet,
        // until the task is done, this line must remain commented
        // { type: 'delete', name: 'mdi-delete-outline' },
      ],
      edit_mode_icons: [
        { type: 'save', name: 'done' },
        { type: 'cancel', name: 'clear' },
      ],
    }
  },

  computed: {
    filteredAvailableCurrencies() {
      const currencies = this.availableCurrencies.filter(
        currency => this.organization.currency !== currency.value
      )

      return currencies.filter(currency =>
        this.removeOrganizationCurrencies(currency.value)
      )
    },

    currencies() {
      const listedCurrencies =
        this.organization?.allowedSiteCurrencies
          ?.filter(c => c.currency !== this.organization.currency)
          .map(currency => ({
            ...currency,
            isViewMode: true,
          })) || []

      return listedCurrencies.slice().reverse()
    },

    canAddCurrency() {
      return Boolean(!this.isLoading && this.exchangeCurrency && this.rate)
    },
  },

  methods: {
    getCurrencyText(currency) {
      const completeCurrency = this.availableCurrencies.find(
        c => c.value === currency.currency
      )
      const exchangeValue = parseFloat(currency.exchangeRateFromGlobal).toFixed(
        2
      )

      return `${exchangeValue} ${completeCurrency.text}`
    },

    deleteCurrency() {
      const updatedCurrencies = Array.from(this.currencies).filter(
        currency => currency._id !== this.currencyToDelete._id
      )

      this.$emit('update', { allowedSiteCurrencies: updatedCurrencies })
      this.showDeleteModal = false
    },

    removeOrganizationCurrencies(currency) {
      const values = this.currencies?.map(exchange => {
        return exchange.currency
      })
      return !values.includes(currency)
    },

    addCurrency() {
      let updatedCurrencies = this.organization.allowedSiteCurrencies
      updatedCurrencies.push({
        exchangeRateFromGlobal: this.rate,
        currency: this.exchangeCurrency,
      })
      this.$emit('update', { allowedSiteCurrencies: updatedCurrencies })
      this.rate = null
      this.exchangeCurrency = null
    },

    editCurrency(currency) {
      const updatedCurrencies = Array.from(
        this.organization?.allowedSiteCurrencies
      )
      const index = updatedCurrencies.findIndex(c => c._id === currency._id)

      updatedCurrencies[index] = currency

      this.$emit('update', { allowedSiteCurrencies: updatedCurrencies })
      this.disableActions = false
    },

    handleClick(type, currency) {
      switch (type) {
        case 'edit':
          this.disableActions = true
          this.currencyToEdit = currency.exchangeRateFromGlobal
          currency.isViewMode = false
          break
        case 'delete':
          this.currencyToDelete = currency
          this.showDeleteModal = true
          break
        case 'save':
          this.editCurrency(currency)
          break
        case 'cancel':
          this.isSaving = true
          currency.exchangeRateFromGlobal = this.currencyToEdit
          currency.isViewMode = true
          this.disableActions = false
          this.isSaving = false
          break
      }
    },

    getEditCurrenciesOptions(currency) {
      const currentOption = this.availableCurrencies.includes(
        c => c.value === currency.currency
      )

      this.availableCurrencies.filter(
        currency =>
          this.removeOrganizationCurrencies(currency.value) || currentOption
      )

      return this.availableCurrencies
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
.icon {
  &:last-child:not(:first-child) {
    margin-left: 20px !important;
  }
}
</style>
